import React, { useState } from 'react';
import { Box, Typography, Stack, Card, CardMedia } from '@mui/material';
import { styled } from '@mui/material/styles';
import { getCharacters } from '../data/characters';
import { useLanguage } from '../data/languages';
import { keyframes } from '@mui/system';

// Keyframe animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const AnimatedBox = styled(Box)(({ delay = 0 }) => ({
  opacity: 0,
  animation: `${fadeIn} 0.5s ease forwards`,
  animationDelay: `${delay}ms`,
}));

// Styled components from NPCSelection
const CharacterCard = styled(Card)(({ theme }) => ({
  width: 'auto',
  height: '40vh',
  flexShrink: 0,
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[8],
  position: 'relative',
  transition: 'all 0.3s ease',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  cursor: 'pointer',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  padding: `${theme.spacing(2)} ${theme.spacing(3)} 0 ${theme.spacing(3)}`,
  '&:hover': {
    boxShadow: theme.shadows[20],
    border: '1px solid rgba(255, 255, 255, 0.4)',
  },
}));

const CharacterImage = styled(CardMedia)(({ theme }) => ({
  height: '85%',
  width: '100%',
  objectFit: 'cover',
  borderRadius: theme.spacing(1),
}));

const CardTitle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(2.5),
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  borderBottomLeftRadius: theme.spacing(2),
  borderBottomRightRadius: theme.spacing(2),
  textAlign: 'center',
}));

const CharacterName = styled(Typography)(({ theme, isActive }) => ({
  fontFamily: "'Playfair Display', serif",
  fontSize: '1.5rem',
  padding: '0.5rem 1rem',
  cursor: 'pointer',
  color: isActive ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.7)',
  transition: 'all 0.3s ease',
  borderLeft: `2px solid ${isActive ? theme.palette.primary.main : 'transparent'}`,
  opacity: 0,
  animation: `${fadeIn} 0.5s ease forwards`,
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  },
}));

const OverlayContent = ({ onSelectNPC }) => {
  const [activeCharacter, setActiveCharacter] = useState(characters[0]);

  return (
    <Box
      sx={{
        height: '50vh',
        width: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        display: 'flex',
        opacity: 0,
        animation: `${fadeIn} 0.3s ease forwards`,
      }}
    >
      {/* Left side - Character Names */}
      <Box
        sx={{
          width: '30%',
          borderRight: '1px solid rgba(0, 0, 0, 0.1)',
          overflow: 'auto',
          py: 4,
          display: 'flex',
          justifyContent: 'flex-end',
          pr: 8,
        }}
      >
        <Stack 
          spacing={1}
          sx={{
            width: 'fit-content',
            minWidth: '200px',
          }}
        >
          {characters.map((character, index) => (
            <CharacterName
              key={character.id}
              isActive={activeCharacter.id === character.id}
              onMouseEnter={() => setActiveCharacter(character)}
              onClick={() => onSelectNPC(character)}
              sx={{ 
                animationDelay: `${100 + index * 50}ms`,
                textAlign: 'right',
                paddingRight: 2,
              }}
            >
              {character.title}
            </CharacterName>
          ))}
        </Stack>
      </Box>

      {/* Right side - Character Preview */}
      <AnimatedBox
        sx={{
          width: '70%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 4,
        }}
        delay={200}
      >
        <AnimatedBox
          sx={{
            position: 'relative',
            width: '400px',
            height: '40vh',
          }}
          delay={300}
        >
          <CharacterCard
            onClick={() => onSelectNPC(activeCharacter)}
          >
            <CharacterImage
              component="img"
              image={activeCharacter.image}
              alt={activeCharacter.title}
            />
            <CardTitle>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                {activeCharacter.title}
              </Typography>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: '1rem',
                  fontStyle: 'italic',
                  color: 'primary.main',
                  opacity: 0.9,
                }}
              >
                {activeCharacter.subtitle}
              </Typography>
            </CardTitle>
          </CharacterCard>
        </AnimatedBox>
      </AnimatedBox>
    </Box>
  );
};

const TransparentSection = ({ onMouseEnter }) => {
  return (
    <Box
      onMouseEnter={onMouseEnter}
      sx={{
        height: '50vh',
        width: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(5px)',
        cursor: 'pointer',
        opacity: 0,
        animation: `${fadeIn} 0.3s ease forwards`,
        animationDelay: '400ms',
      }}
    />
  );
};

export default function NPCOverlay({ isOpen, onClose, onSelectNPC, onMouseLeave }) {
  const currentLang = useLanguage(); // 响应式语言获取
  const characters = getCharacters(currentLang);
  
  if (!isOpen) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        transform: `translateY(${isOpen ? '0' : '-100%'})`,
        transition: 'transform 0.3s ease-in-out',
        pt: '64px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <OverlayContent onSelectNPC={onSelectNPC} />
      <TransparentSection onMouseEnter={onMouseLeave} />
    </Box>
  );
} 